import { useFormik } from "formik";
import { RootState } from "../../store";
import { CustomSelect, Modal, TextArea, TextInput } from "..";
import * as Yup from "yup";
import { connect, ConnectedProps } from "react-redux";
import { useEffect, useState, useCallback } from "react";
import { VIDEOFLAVOR, AUDIOFLAVOR } from "@/utils/constants/uiConstants";

interface IAddPreset {
  preset_type?: string;
  presetData?: any;
  isEdit?: boolean;
  handleSave: any;
  bunchProductList?: any;
  onHasChanges?: (hasChanges: boolean) => void;
}

interface EmbedForm {
  presetName?: string;
  desc?: string;
  visibility?: string[];
}

function formatResolution(value: any) {
  const [resLabel, widthStr] = value.split("_");
  const height = parseInt(resLabel, 10); // e.g., "1080p" => 1080
  const width = parseInt(widthStr, 10);

  if (isNaN(height) || isNaN(width)) return value;

  return `${resLabel}(${width}x${height})`;
}
const AddPreset = ({
  preset_type,
  presetData,
  handleSave,
  isEdit,
  bunchProductList,
  onHasChanges,
  presetList,
}: Props) => {
  const [flavours, setFlavours] = useState<any[]>(presetData?.flavours || []);

  // Store initial values for comparison
  const initialValues = {
    presetName: presetData?.name || "",
    desc: presetData?.description || "",
    visibility: presetData?.team || [],
    flavours: presetData?.flavours || [],
  };

  const flavoursList = preset_type === "video" ? VIDEOFLAVOR : AUDIOFLAVOR;
  const formik = useFormik<EmbedForm>({
    initialValues: {
      presetName: presetData?.name || "",
      desc: presetData?.description || "",
      visibility: presetData?.team || [],
    },
    validationSchema: Yup.object({
      presetName: Yup.string()
        .required("Preset Name is required")
        .test(
          "unique-preset-name",
          "Same Name is not allowed",
          function (value) {
            if (!value || !presetList || presetList.length === 0) return true;

            const currentVisibility = this.parent.visibility || [];

            // If no visibility is selected, don't check for duplicates
            // if (currentVisibility.length === 0) {
            //   return true;
            // }

            const duplicateExists = presetList.some((presetItem: any) => {
              // Check if names match
              if (presetItem.name !== value) return false;

              // Check if type matches
              if (presetItem.type !== "processing") return false;

              // Skip self in edit mode
              if (isEdit && presetItem.id === presetData?.id) return false;

              // Check visibility/team match - compare arrays properly
              const itemteam = presetItem.team || [];

              // If item has no team, don't match
              if (itemteam.length === 0) {
                return false;
              }

              // Check if any visibility value matches any team value
              return currentVisibility.some((vis: string) =>
                itemteam.includes(vis)
              );
            });

            return !duplicateExists;
          }
        ),
      visibility: Yup.array().of(Yup.string()).min(1, "Visibility is required"),
    }),
    onSubmit: (values) => {
      const data = {
        id: presetData?.id,

        name: values.presetName,
        description: values.desc,
        team: values.visibility,
        sub_type: preset_type,
        flavours: flavours,
        status: 1,
        type: "processing",
      };
      handleSave(data, isEdit);
    },
  });

  // Function to check if there are changes
  const checkForChanges = useCallback(() => {
    if (!onHasChanges) return;

    const currentValues = {
      presetName: formik.values.presetName,
      desc: formik.values.desc,
      visibility: formik.values.visibility,
      flavours: flavours,
    };

    let hasChanges = false;

    if (isEdit) {
      // For edit mode, compare with initial values
      hasChanges =
        currentValues.presetName !== initialValues.presetName ||
        currentValues.desc !== initialValues.desc ||
        JSON.stringify([...(currentValues?.visibility || [])].sort()) !==
          JSON.stringify([...(initialValues?.visibility || [])].sort()) ||
        JSON.stringify(currentValues?.flavours) !==
          JSON.stringify(initialValues?.flavours);
    } else {
      // For create mode, check if any field has been filled
      hasChanges =
        (currentValues.presetName && currentValues.presetName.trim() !== "") ||
        (currentValues.desc && currentValues.desc.trim() !== "") ||
        (currentValues.visibility && currentValues.visibility.length > 0) ||
        (currentValues.flavours && currentValues.flavours.length > 0);
    }

    onHasChanges(hasChanges);
  }, [isEdit, onHasChanges, formik.values, flavours, initialValues]);

  const handleOnSubmit = async (e: any) => {
    e.preventDefault();
    formik.setTouched({
      presetName: true,
      visibility: true,
    });
    const errors = await formik.validateForm();
    if (errors) {
      console.log(errors, "errors");
    }
    if (Object.keys(errors).length > 0) {
      return;
    }
    formik.submitForm();
  };

  const handleFlavorCheckbox = (
    flavorId: number,
    flavorName: string,
    isChecked: boolean
  ) => {
    setFlavours((prev) => {
      if (isChecked) {
        return [
          ...prev,
          { id: Number(flavorId), name: flavorName, formats: [] },
        ];
      } else {
        return prev.filter((item) => Number(item.id) !== Number(flavorId));
      }
    });
  };
  const getBasePermission = (permission: string): string => {
    return permission;
  };

  function processPermissionForSelect(array: string[]) {
    const newarray = array?.map((str) => {
      return getBasePermission(str);
    });
    return newarray;
  }

  const handlePermissionChange = (values: string[], key: string) => {
    const newProducers = values?.map((value) => value);

    formik.setFieldValue(key, newProducers);
  };

  const handleFormatCheckbox = (
    flavorId: number,
    format: string,
    isChecked: boolean
  ) => {
    setFlavours((prev) => {
      return prev.map((item) => {
        if (Number(item.id) === Number(flavorId)) {
          if (isChecked) {
            return {
              ...item,
              formats: [...item.formats, format],
            };
          } else {
            return {
              ...item,
              formats: item.formats.filter((f: string) => f !== format),
            };
          }
        }
        return item;
      });
    });
  };

  // Check for changes whenever form values or flavours change
  useEffect(() => {
    checkForChanges();
  }, [formik.values, flavours]);

  useEffect(() => {
    const handlecreate = (e: any) => {
      handleOnSubmit(e);
    };
    window.addEventListener("createMetaPreset", handlecreate);
    return () => {
      window.removeEventListener("createMetaPreset", handlecreate);
    };
  });

  return (
    <>
      <div className="d-flex flex-column  pb-2">
        <div
          className="row bg-light p-3 position-sticky z-2 m-0"
          style={{ top: "-16px" }}
        >
          <div className="col-md-4">
            <TextInput
              label="Preset Name"
              name="presetName"
              value={formik?.values?.presetName || ""}
              onChange={(e: any) => {
                formik.setFieldValue("presetName", e.target.value);
              }}
              onBlur={formik.handleBlur}
              showRequired={true}
              placeholder="Enter Preset Name"
              error={formik.errors.presetName as string}
              touched={formik.touched.presetName as boolean}
            />
          </div>

          <div className="col-md-4">
            <CustomSelect
              // value={formik.values.visibility}
              value={processPermissionForSelect(
                formik?.values?.visibility || []
              )}
              // value={prevVideoCredit}
              handleChange={(newValue: any) => {
                handlePermissionChange(
                  newValue.map((option: any) => option.value),
                  "visibility"
                );
              }}
              label="team"
              multiSelect={true}
              placeholder="Team/Property"
              checkBox={true}
              showRequired={true}
              options={
                bunchProductList?.map((item: any) => ({
                  label: item?.name,
                  value: item?.id,
                })) || []
              }
              updatedOptions={
                bunchProductList?.map((item: any) => ({
                  label: item?.name,
                  value: item?.id,
                })) || []
              }
              hideArrow={true}
              error={formik.errors.visibility as string}
              touched={formik.touched.visibility as boolean}
              handleBlur={formik.handleBlur}
            />
          </div>
          <div className="col-md-4">
            <TextInput
              label="Description"
              placeholder="Enter Here"
              name="desc"
              onChange={(e) => {
                formik.setFieldValue("desc", e.target.value);
              }}
              value={formik.values.desc || ""}
              onBlur={formik.handleBlur}
              error={formik.errors.desc}
              touched={formik.touched.desc}
              showRequired={false}
            />
          </div>
        </div>
        <div
          style={{ width: "100%" }}
          className="d-flex flex-column border-end"
        >
          <div className="d-flex flex-row justify-content-between border-bottom pb-2">
            <div>
              <span className="fw-bold text-dark">Flavours</span>
            </div>
            <div className="d-flex gap-4" style={{ marginRight: "70px" }}>
              <div style={{ width: "30px", textAlign: "center" }}>
                <span className="fw-bold text-dark">HLS</span>
              </div>
              <div style={{ width: "30px", textAlign: "center" }}>
                <span className="fw-bold text-dark">MP4</span>
              </div>
              <div style={{ width: "30px", textAlign: "center" }}>
                <span className="fw-bold text-dark">WEBM</span>
              </div>
              <div style={{ width: "30px", textAlign: "center" }}>
                <span className="fw-bold text-dark">MP3</span>
              </div>
            </div>
          </div>
          <div className="d-flex flex-column gap-3 pt-2">
            {flavoursList.map((item: any) => (
              <div className="d-flex flex-row align-item-center justify-content-between border-bottom pb-3">
                <div className="d-flex gap-2 align-items-center">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    style={{ width: "18px", height: "18px" }}
                    onChange={(e) =>
                      handleFlavorCheckbox(
                        item.value,
                        item.label,
                        e.target.checked
                      )
                    }
                    checked={flavours.some(
                      (f) => Number(f.id) === Number(item.value)
                    )}
                  />
                  <span>{formatResolution(item.label)}</span>
                </div>
                <div className="d-flex gap-4" style={{ marginRight: "70px" }}>
                  <div style={{ width: "30px", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      style={{ width: "18px", height: "18px" }}
                      className="form-check-input"
                      onChange={(e) =>
                        handleFormatCheckbox(
                          item.value,
                          "HLS",
                          e.target.checked
                        )
                      }
                      checked={flavours.some(
                        (f) =>
                          Number(f.id) === Number(item.value) &&
                          f.formats.includes("HLS")
                      )}
                      disabled={
                        !flavours.some(
                          (f) => Number(f.id) === Number(item.value)
                        )
                      }
                    />
                  </div>
                  <div style={{ width: "30px", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      style={{ width: "18px", height: "18px" }}
                      className="form-check-input"
                      onChange={(e) =>
                        handleFormatCheckbox(
                          item.value,
                          "MP4",
                          e.target.checked
                        )
                      }
                      checked={flavours.some(
                        (f) =>
                          Number(f.id) === Number(item.value) &&
                          f.formats.includes("MP4")
                      )}
                      disabled={
                        !flavours.some(
                          (f) => Number(f.id) === Number(item.value)
                        )
                      }
                    />
                  </div>
                  <div style={{ width: "30px", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      style={{ width: "18px", height: "18px" }}
                      className="form-check-input"
                      onChange={(e) =>
                        handleFormatCheckbox(
                          item.value,
                          "WEBM",
                          e.target.checked
                        )
                      }
                      checked={flavours.some(
                        (f) =>
                          Number(f.id) === Number(item.value) &&
                          f.formats.includes("WEBM")
                      )}
                      disabled={
                        !flavours.some(
                          (f) => Number(f.id) === Number(item.value)
                        )
                      }
                    />
                  </div>
                  <div style={{ width: "30px", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      style={{ width: "18px", height: "18px" }}
                      className="form-check-input"
                      onChange={(e) =>
                        handleFormatCheckbox(
                          item.value,
                          "MP3",
                          e.target.checked
                        )
                      }
                      checked={flavours.some(
                        (f) =>
                          Number(f.id) === Number(item.value) &&
                          f.formats.includes("MP3")
                      )}
                      disabled={
                        !flavours.some(
                          (f) => Number(f.id) === Number(item.value)
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

const mapState = (state: RootState) => ({
  presetList: state.media.presetList.list,
});
const mapDispatch = {};

const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector> & IAddPreset;
export default connector(AddPreset);
